import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCompanionStore } from '../../store/companionStore';
import styles from './CompanionTranscript.module.css';

function fmt(sec: number): string {
  if (!isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Lecture transcript as a tab inside the Companion dock — folded into the
 * existing right rail rather than living as a third column beside the video.
 * Reads the segments + active index + seekTo that the LecturePlayer publishes
 * to the store, so it stays in sync with and can scrub the <video>.
 *
 * Locked down for content protection (non-selectable + copy/cut/contextmenu/
 * drag blocked), matching the server-side gate on /transcript (auth +
 * watermark + rate-limit + access-log). Click-to-seek still works.
 */
const CompanionTranscript: React.FC = () => {
  const ctx = useCompanionStore((s) => s.lectureContext);
  const activeSeg = useCompanionStore((s) => s.lectureActiveSeg);
  const activeRef = useRef<HTMLLIElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(false);

  const jumpToCurrent = useCallback(() => {
    const host = scrollRef.current;
    const row = activeRef.current;
    if (!host || !row) return;
    const bounds = host.getBoundingClientRect();
    const target = row.getBoundingClientRect();
    if (target.top < bounds.top) host.scrollTop += target.top - bounds.top;
    else if (target.bottom > bounds.bottom) host.scrollTop += target.bottom - bounds.bottom;
  }, []);

  // Playback can highlight without moving the transcript. Follow is opt-in
  // and only adjusts this dock's own scroll position.
  useEffect(() => {
    if (follow) jumpToCurrent();
  }, [activeSeg, follow, jumpToCurrent]);

  if (!ctx) return null;

  return (
    <div
      ref={scrollRef}
      className={styles.transcript}
      aria-label="Transcript"
      onWheel={() => setFollow(false)}
      onTouchStart={() => setFollow(false)}
      onKeyDown={(event) => {
        const inFollowActions = event.target instanceof HTMLElement
          && Boolean(event.target.closest(`.${styles.followActions}`));
        if (!inFollowActions && ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
          setFollow(false);
        }
      }}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <div className={styles.followActions}>
        <button type="button" aria-pressed={follow} onClick={() => setFollow(!follow)}>
          Follow playback {follow ? 'on' : 'off'}
        </button>
        {!follow && activeSeg >= 0 && (
          <button type="button" onClick={jumpToCurrent}>Jump to current</button>
        )}
      </div>
      <ol className={styles.segments}>
        {ctx.segments.map((seg, i) => (
          <li key={`${seg.start}-${i}`} ref={i === activeSeg ? activeRef : undefined}>
            <button
              type="button"
              className={i === activeSeg ? `${styles.seg} ${styles.segActive}` : styles.seg}
              onClick={() => ctx.seekTo(seg.start)}
              aria-current={i === activeSeg || undefined}
            >
              <span className={styles.segTime}>{fmt(seg.start)}</span>
              <span className={styles.segText}>{seg.text}</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default CompanionTranscript;
